import { useCallback, useEffect, useState } from "react";

import { AssistitoRepository } from "core/data";
import { IRegisteredUserData } from "core/interfaces/data/IRegisterRequest";
import i18n from "utils/i18n";
import IDatiUtente from "pages/AreaRiservata/DatiUtente/IDatiUtente";
import useWindowDimensions from "utils/WindowSize";
import { Alert, Descriptions, Input, Skeleton, Spin, Switch } from "antd";
import { FaEdit } from "react-icons/fa";
import { CardFamiliare, CustomButton, HrText } from "core/interfaces/components";
import { PagesEnum } from "types/PagesEnum";

import confirm from "antd/lib/modal/confirm";
import "./DatiUtente.css";
import { IFamiliare } from "core/interfaces/data/IAssistitoRequest";
import { validatePhoneNumberFormat } from "utils/Validate";
import { performAsyncFunction } from "utils/AsyncFunctionUtility";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";
import { buttonTypeCssClassEnum } from "components/CustomButton/CustomButton";
import NavigationManager from "utils/NavigationManager";
import { Enhanced } from "utils/ReactUtils";
import CustomTranslation from "components/CustomTranslation/CustomTranslation";
import { AssistitoEnums } from "types/AssistitoEnums";

const DatiUtente: React.FC<IDatiUtente> = ({ utente, t, sessionInfo, handleError, dispatch }) => {
	const { GoToPage } = NavigationManager(sessionInfo);

	const [loading, setLoading] = useState<boolean>(true);
	const [editMode, setEditMode] = useState<boolean>(false);
	const [registeredUser, setRegisteredUser] = useState<IRegisteredUserData | undefined>(undefined);
	const [checkedSMS, setCheckedSMS] = useState<boolean>(false);
	const [checkedMail, setCheckedMail] = useState<boolean>(false);
	const [phoneNumber, setPhoneNumber] = useState<string>();
	const [phoneNumberValidationState, setPhoneNumberValidationState] = useState<boolean>(true);

	const [isError, setIsError] = useState<boolean>(false);
	const [spinning, setSpinning] = useState<boolean>(false);

	const { width } = useWindowDimensions();

	const GetUserData = useCallback(async () => {
		try {
			var loginData = await AssistitoRepository.GetLoggedUserData({
				IdSessione: sessionInfo.sessione?.idSessione,
				culture: i18n.language,
			});

			return loginData;
		} catch (err: any) {
			setIsError(true);
			return undefined;
		}
	}, [sessionInfo.sessione?.idSessione]);

	useEffect(() => {
		performAsyncFunction(async () => {
			setLoading(true);

			const user = await GetUserData();

			if (user !== undefined) {
				setCheckedSMS(user!.ConsensoSMS);
				setCheckedMail(user!.ConsensoMail);
				setPhoneNumber(user?.TelefonoContatti);

				setRegisteredUser(user);
				setIsError(false);
			} else {
				setIsError(true);
			}
			setLoading(false);
		}, handleError);
	}, [GetUserData, handleError]);

	const onCancelClick = async () => {
		// const user = await GetUserData();

		// setCheckedSMS(user!.ConsensoSMS);
		// setCheckedMail(user!.ConsensoMail);
		// setPhoneNumber(user!.TelefonoContatti);

		// setRegisteredUser(user);
		setEditMode(!editMode);
	};

	const onSaveClick = async () => {
		try {
			if (phoneNumberValidationState) {
				setSpinning(true);
				// salvataggio dei dati dell'utente

				var changeSuccess = await AssistitoRepository.UpdateUserData({
					Telefono: phoneNumber!,
					ConsensoSMS: checkedSMS,
					ConsensoMail: checkedMail,
					IdSessione: sessionInfo.sessione?.idSessione,
					culture: i18n.language,
				});
				if (changeSuccess) {
					//recpero le informazioni dell' utente
					const user = await GetUserData();
					setRegisteredUser(user);
					setEditMode(!editMode);
					setSpinning(false);
				}
			}
		} catch (err: any) {
			setIsError(true);
			setSpinning(false);
		}
	};

	const onPhoneNumberChanged = (value: any) => {
		if (value?.currentTarget && validatePhoneNumberFormat(value.currentTarget.value!)) {
			setPhoneNumber(value.currentTarget.value!);
			setPhoneNumberValidationState(true);
		} else {
			setPhoneNumberValidationState(false);
		}
	};

	const onChangePasswordClick = () => {
		if (editMode) {
			confirm({
				title: t("reservedArea.datiUtente.popup.title"),
				content: t("reservedArea.datiUtente.popup.text"),
				onOk() {
					GoToPage(PagesEnum.ChangePassword);
				},
				okText: t("reservedArea.datiUtente.popup.confirm"),
				cancelText: t("reservedArea.datiUtente.popup.cancel"),
				cancelButtonProps: { className: buttonTypeCssClassEnum[CustomButtonType.negative] },
				okButtonProps: { className: buttonTypeCssClassEnum[CustomButtonType.positive] },
			});
		} else {
			GoToPage(PagesEnum.ChangePassword);
		}
	};

	return (
		<>
			<div id="datiUtente">
				{loading ? (
					<Skeleton active />
				) : !isError ? (
					<>
						<HrText text={t("reservedArea.datiUtente.title.userData")}></HrText>
						<div className={`card-container`} key={utente ? utente.Matricola : -1}>
							<CardFamiliare
								key={"UserCard" + utente ? utente?.Matricola : -1}
								familiare={
									utente
										? utente
										: ({
												CodiceFiscale: registeredUser!.CodiceFiscale,
												Matricola: "-1",
												Sesso: registeredUser?.Sesso === "M" ? AssistitoEnums.Maschio : AssistitoEnums.Fremminia,
												DataNascita: registeredUser?.DataNascita.toLocaleString()!,
												Nome: registeredUser?.Nome!,
												Cognome: registeredUser?.Cognome!,
												UtenteIsRichiedente: false,
												LegameUtente: undefined,
												IndirizzoResidenza: registeredUser?.IndirizzoResidenza!,
												TelDomicilio: registeredUser?.TelefonoContatti!,
										  } as IFamiliare)
								}
								selected={undefined}
								showAddressDetails={true}
								showLegame={false}
							/>
						</div>
						<HrText text={t("reservedArea.datiUtente.title.accountData")}></HrText>
						<div className="datiutente datiaccount ">
							<div className="form footer">
								<Descriptions column={1} layout={width > 330 ? "horizontal" : "vertical"}>
									<Descriptions.Item
										label={t("reservedArea.datiUtente.userName")}
										className="subtitle"
										labelStyle={{ width: width > 330 ? 100 : 185 }}
									>
										{registeredUser?.Email}
									</Descriptions.Item>
									<Descriptions.Item
										label={t("reservedArea.datiUtente.password")}
										className="subtitle"
										labelStyle={{ width: width > 330 ? 100 : 185 }}
									>
										********
									</Descriptions.Item>
								</Descriptions>
							</div>
							<div className="buttons">
								<CustomButton
									className={"button-edit ant-btn"}
									text={t("reservedArea.datiUtente.changePassword")}
									OnClick={onChangePasswordClick}
									icon={<FaEdit />}
									buttonType={CustomButtonType.positive}
								/>
							</div>
						</div>

						<HrText text={t("reservedArea.datiUtente.title.contactData")}></HrText>
						<Spin spinning={spinning}>
							<div className={"datiutente daticontatto" + (editMode ? " edit" : "")}>
								<div className="form footer">
									<Descriptions column={1} layout={width > 330 ? "horizontal" : "vertical"}>
										<Descriptions.Item label={t("reservedArea.datiUtente.phone")} className="footer" labelStyle={{ width: width > 330 ? 100 : 185 }}>
											{editMode ? (
												<Input
													type="tel"
													prefix="+39"
													maxLength={10}
													defaultValue={phoneNumber}
													style={{ width: "fit-content" }}
													onChange={onPhoneNumberChanged}
													status={phoneNumberValidationState ? "" : "error"}
												></Input>
											) : (
												<Input
													type="tel"
													prefix="+39"
													defaultValue={phoneNumber}
													style={{ width: "fit-content" }}
													readOnly={true}
													disabled={true}
													id={"tel-dati-utente-disabled"}
												></Input>
											)}
										</Descriptions.Item>

										{window.CONFIG.REACT_APP_CONSENSI.SMS.ENABLED && (
											<Descriptions.Item
												label={t("reservedArea.datiUtente.SMSpermiss")}
												className="subtitle"
												labelStyle={{ width: width > 330 ? 185 : 100 }}
											>
												<Switch
													disabled={!editMode}
													checked={checkedSMS}
													onClick={() => {
														setCheckedSMS(!checkedSMS);
													}}
												/>
											</Descriptions.Item>
										)}
										<Descriptions.Item
											label={t("reservedArea.datiUtente.Mailpermiss")}
											className="subtitle"
											labelStyle={{ width: width > 330 ? 185 : 100 }}
										>
											<Switch
												disabled={!editMode}
												checked={checkedMail}
												onClick={() => {
													setCheckedMail(!checkedMail);
												}}
											/>
										</Descriptions.Item>
									</Descriptions>
								</div>

								<div className="buttons ">
									{editMode ? (
										<>
											<CustomButton
												className={"editing-buttons button-negative"}
												OnClick={onCancelClick}
												buttonType={CustomButtonType.negative}
												text={t("reservedArea.datiUtente.cancel")}
											/>
											<CustomButton
												className={"editing-buttons button-save"}
												OnClick={onSaveClick}
												buttonType={CustomButtonType.positive}
												text={t("reservedArea.datiUtente.save")}
											/>
										</>
									) : (
										<CustomButton
											className={"button-edit"}
											text={t("reservedArea.datiUtente.edit")}
											OnClick={() => {
												setEditMode(!editMode);
											}}
											icon={<FaEdit />}
											buttonType={CustomButtonType.positive}
										/>
									)}
								</div>
							</div>
						</Spin>
						<br />
						<div className="datiutente integrazione">
							<CustomTranslation t={t} type="contatti" i18Key={"reservedArea.datiUtente.integration"} />
						</div>
					</>
				) : (
					<Alert message={t("errorMessages.base")} type="error" showIcon banner />
				)}
			</div>
		</>
	);
};
export default Enhanced(DatiUtente, { componentNamespace: "DatiUtente" });
